import React, { useState, useEffect } from 'react'
import Modal from 'aldoo-ra/Modal' // Import your actual Modal component
import Button from 'aldoo-ra/Button' // Import your actual Button component
import ToggleSwitch from 'aldoo-ra/ToggleSwitch' // Import your ToggleSwitch component

const LanguageAutoTranslateDialog = ({
  isOpen,
  onClose,
  language,
  controller,
}) => {
  const [translateMissingOnly, setTranslateMissingOnly] = useState(true) // Initially "Missing keys only"
  const [isCompleted, setIsCompleted] = useState(false) // To track when the operation is completed
  const [isStopped, setIsStopped] = useState(false) // To track when the translation is stopped
  const [error, setError] = useState('') // Track error state
  const {
    languageAutoTranslateData,
    isAutoTranslating,
    autoTranslateLanguage,
    stopAutoTranslateLanguage,
  } = controller

  const handleToggleMissingOnly = () => {
    if (!translateMissingOnly) {
      setTranslateMissingOnly(true)
    }
  }

  const handleToggleEverything = () => {
    if (translateMissingOnly) {
      setTranslateMissingOnly(false)
    }
  }

  // Start translation by calling the controller method
  const startTranslation = async () => {
    setIsCompleted(false) // Reset completion status
    setIsStopped(false) // Reset stop status
    setError(null) // Reset error before starting translation

    try {
      // Call the autoTranslateLanguage method from the controller
      await autoTranslateLanguage(
        controller.primaryLanguage,
        language.code,
        translateMissingOnly ? 'all_missing' : 'all',
      )
    } catch (err) {
      setError('Failed to start translation')
    }
  }

  const stopTranslation = async () => {
    // Handle stopping translation
    setIsStopped(true) // Set translation as stopped
    setError(null) // Clear any previous errors
    await stopAutoTranslateLanguage() // Call the stopTaskProgress method from the controller
  }

  useEffect(() => {
    if (!isOpen) {
      setError(null) // Reset error on modal close
      setIsCompleted(false) // Reset completion status
      setIsStopped(false) // Reset stop status
    }

    // Update completion state when translation completes
    if (
      languageAutoTranslateData &&
      languageAutoTranslateData.status === 'completed'
    ) {
      setIsCompleted(true)
      // Reset the languageAutoTranslateData after completion
      controller.setLanguageAutoTranslateData(null)
    }

    // Set error if task fails
    if (languageAutoTranslateData && languageAutoTranslateData.error) {
      setError(
        languageAutoTranslateData.error ||
          'An error occurred during translation',
      )
    }
  }, [isOpen, languageAutoTranslateData])

  // Calculate progress percentage based on task progress/total and report rounded
  const progress = languageAutoTranslateData
    ? Math.round(
        (languageAutoTranslateData?.progress /
          languageAutoTranslateData?.total) *
          100,
      )
    : 0

  // Resolve the appropriate action for the button
  const resolveAction = () => {
    if (isCompleted) return startTranslation // When completed, button should "Run again"
    if (isStopped) return startTranslation // If stopped, button should "Restart"
    if (isAutoTranslating) return stopTranslation // Stop translation when ongoing
    return startTranslation // Start translation when idle
  }

  // Resolve the appropriate label for the button
  const resolveLabel = () => {
    if (error) {
      return 'Restart'
    } else if (isStopped) {
      return 'Restart' // If stopped, show "Restart"
    } else if (isAutoTranslating) {
      return 'Stop'
    } else if (isCompleted) {
      return 'Run again'
    } else {
      return 'Start'
    }
  }

  // Resolve the centered label based on the current status
  const resolveCenterLabel = () => {
    if (error) {
      return 'Oops...'
    } else if (isAutoTranslating) {
      return 'Translating...'
    } else if (isStopped) {
      return 'Stopped'
    } else if (isCompleted) {
      return 'Completed'
    } else {
      return 'Press Start to translate this language'
    }
  }

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      className="relative sm:w-[400px] sm:max-w-[500px] h-full md:h-auto"
      title={`Auto translate - ${language?.name}`}
    >
      <div className="p-4 text-admin_text">
        {/* ToggleSwitches for Missing Keys Only and Everything */}
        <div className="mb-4 flex flex-col space-y-10">
          <div className="flex items-center">
            <ToggleSwitch
              variant="admin"
              isOn={translateMissingOnly}
              onChange={handleToggleMissingOnly}
            />
            <span className="ml-2 font-semibold">Missing keys only</span>
          </div>
          <div className="flex items-center">
            <ToggleSwitch
              variant="admin"
              isOn={!translateMissingOnly}
              onChange={handleToggleEverything}
            />
            <span className="ml-2 font-semibold">Everything</span>
          </div>
        </div>

        {/* Freeze warning notice */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-blue-600">❄️</span>
            <span className="text-sm font-medium text-blue-800">Frozen Translations</span>
          </div>
          <p className="text-xs text-blue-600">
            Translations marked as frozen (❄️) will be automatically skipped during auto-translation to preserve your manual edits.
            {!translateMissingOnly && " Use the Missing keys only option to avoid overwriting existing translations."}
          </p>
        </div>

        <div className="h-[100px] items-center mt-10">
          {/* Centered Label - Change text based on error, translating status, completion, and stop */}
          <div className="text-center font-semibold mb-2">
            {resolveCenterLabel()}
          </div>
          {/* Show Progress Bar only when translating */}
          {!isStopped && isAutoTranslating && (
            <div className="my-5">
              <div className="w-full bg-admin_neutral_1 rounded h-4 relative">
                <div
                  className="bg-admin_primary h-full rounded"
                  style={{
                    width: `${progress}%`,
                  }}
                ></div>
                <span className="absolute right-0 top-0 text-xs text-admin_text pr-2">
                  {progress}%
                </span>
              </div>
            </div>
          )}

          {/* Error Message (if any) - Keep space even when there is no error */}
          {error ? <p className="text-admin_error text-center">{error}</p> : null}
        </div>

        {/* Start, Stop, or Run Again Button */}
        <div className="flex justify-center mt-4">
          <Button
            variant="admin_primary"
            className="w-full sm:w-32"
            onClick={resolveAction()}
          >
            {resolveLabel()} {/* Use resolveLabel for button label */}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default LanguageAutoTranslateDialog
